/**
 * Stable ids on lines (#337). A paragraph or heading gets a `lineId` the first
 * time it is filed into a named list; filed items point at it by that id.
 * A copy (paste, split, any insert that duplicates an id) has no id: it is not
 * filed anywhere until someone files it. A line moved by drag keeps its id and
 * so its filings. A line inside a list never carries one: items are not sources.
 */
import { Extension } from '@tiptap/core';
import { Plugin, PluginKey, type EditorState, type Transaction } from '@tiptap/pm/state';
import { Fragment, Slice, type Node as PmNode } from '@tiptap/pm/model';
import type { EditorView } from '@tiptap/pm/view';

export const LINE_SOURCES = new Set(['paragraph', 'heading']);
export const newLineId = () => crypto.randomUUID();

export function stripLineIds(slice: Slice): Slice {
  const map = (fragment: Fragment): Fragment => {
    const nodes: PmNode[] = [];
    fragment.forEach((node) => {
      if (node.isText) { nodes.push(node); return; }
      nodes.push(LINE_SOURCES.has(node.type.name) && node.attrs.lineId
        ? node.type.create({ ...node.attrs, lineId: null }, map(node.content), node.marks)
        : node.copy(map(node.content)));
    });
    return Fragment.fromArray(nodes);
  };
  return new Slice(map(slice.content), slice.openStart, slice.openEnd);
}

function pastedLines(slice: Slice, view: EditorView): Slice {
  return (view as unknown as { dragging?: { move?: boolean } | null }).dragging?.move ? slice : stripLineIds(slice);
}

/** Every copy of an id but one loses it; a line inside a list always does. */
function fixLineIds(old: EditorState, state: EditorState): Transaction | null {
  const oldText = new Map<string, string>();
  old.doc.descendants((n) => {
    if (LINE_SOURCES.has(n.type.name) && n.attrs.lineId) oldText.set(n.attrs.lineId, n.textContent);
    return true;
  });
  const seen = new Map<string, Array<{ pos: number; node: PmNode; inList: boolean }>>();
  state.doc.descendants((n, pos, parent) => {
    if (LINE_SOURCES.has(n.type.name) && n.attrs.lineId) {
      const at = seen.get(n.attrs.lineId) ?? [];
      at.push({ pos, node: n, inList: parent?.type.name === 'namedListItem' });
      seen.set(n.attrs.lineId, at);
    }
    return true;
  });
  let tr: Transaction | null = null;
  for (const [id, at] of seen) {
    const outside = at.filter((a) => !a.inList);
    // Keep the copy that still reads as it did; every other copy is new.
    const keep = outside.find((a) => a.node.textContent === oldText.get(id)) ?? outside[0];
    for (const a of at) {
      if (a === keep) continue;
      tr ??= state.tr;
      tr.setNodeMarkup(a.pos, undefined, { ...a.node.attrs, lineId: null });
    }
  }
  return tr;
}

export const LineIdExtension = Extension.create({
  name: 'lineId',

  addGlobalAttributes() {
    return [{
      types: [...LINE_SOURCES],
      attributes: {
        lineId: {
          default: null,
          keepOnSplit: false,
          parseHTML: (el) => el.getAttribute('data-line-id') || null,
          renderHTML: (attrs) => (attrs.lineId ? { 'data-line-id': attrs.lineId } : {}),
        },
      },
    }];
  },

  addProseMirrorPlugins() {
    return [new Plugin({
      key: new PluginKey('lineIds'),
      props: { transformPasted: pastedLines },
      appendTransaction: (trs, old, state) => (trs.some((t) => t.docChanged) ? fixLineIds(old, state) : null),
    })];
  },
});
